import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';
import Footer from './Footer';

const ChangePassword: React.FC = () => {
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');

    // Check if user should be on this page
    useEffect(() => {
        const mustChange = localStorage.getItem('mustChangePassword');
        const token = apiClient.getToken();
        
        if (!token) {
            navigate('/login');
            return;
        }
        
        if (!mustChange) {
            navigate('/');
            return;
        }
    }, [navigate]);

    // Password strength calculation
    useEffect(() => {
        if (!newPassword) {
            setPasswordStrength('weak');
            return;
        }

        let strength = 0;
        if (newPassword.length >= 8) strength++;
        if (/[a-z]/.test(newPassword)) strength++;
        if (/[A-Z]/.test(newPassword)) strength++;
        if (/[0-9]/.test(newPassword)) strength++;
        if (/[^a-zA-Z0-9]/.test(newPassword)) strength++;

        if (strength <= 2) setPasswordStrength('weak');
        else if (strength <= 4) setPasswordStrength('medium');
        else setPasswordStrength('strong');
    }, [newPassword]);

    const getStrengthColor = () => {
        switch (passwordStrength) {
            case 'weak': return 'bg-red-500';
            case 'medium': return 'bg-yellow-500';
            case 'strong': return 'bg-green-500';
        }
    };

    const getStrengthWidth = () => {
        switch (passwordStrength) {
            case 'weak': return 'w-1/3';
            case 'medium': return 'w-2/3';
            case 'strong': return 'w-full';
        }
    };

    const validatePassword = (): string | null => {
        if (newPassword.length < 8) {
            return 'Password must be at least 8 characters long';
        }
        if (!/[A-Z]/.test(newPassword)) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!/[a-z]/.test(newPassword)) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!/[0-9]/.test(newPassword)) {
            return 'Password must contain at least one number';
        }
        if (!/[^a-zA-Z0-9]/.test(newPassword)) {
            return 'Password must contain at least one special character';
        }
        if (newPassword !== confirmPassword) {
            return 'Passwords do not match';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validatePassword();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsLoading(true);

        try {
            await apiClient.changePassword(currentPassword, newPassword);
            
            // Clear the flag
            localStorage.removeItem('mustChangePassword');

            // Show success and redirect
            toast.success('Password changed successfully! Redirecting to dashboard...');
            navigate('/');
        } catch (err: any) {
            console.error('Change password error:', err);
            setError(err.message || 'Failed to change password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        apiClient.logout();
        localStorage.removeItem('mustChangePassword');
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                        <Lock className="h-8 w-8" style={{ color: '#69BE28' }} />
                    </div>
                    <h2 className="mt-6 text-3xl font-bold text-gray-900">
                        Change Password Required
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        For security reasons, you must change your password before accessing the system.
                    </p>
                </div>

                {/* Form */}
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {/* Current Password */}
                        <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                                Current Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="currentPassword"
                                    name="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    required
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:z-10 sm:text-sm"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    placeholder="Enter your current password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                >
                                    {showCurrentPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* New Password */}
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    required
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:z-10 sm:text-sm"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    placeholder="Enter your new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </button>
                            </div>
                            
                            {/* Password Strength Indicator */}
                            {newPassword && (
                                <div className="mt-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs text-gray-600">Password Strength:</span>
                                        <span className={`text-xs font-medium ${
                                            passwordStrength === 'weak' ? 'text-red-600' :
                                            passwordStrength === 'medium' ? 'text-yellow-600' :
                                            'text-green-600'
                                        }`}>
                                            {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor()} ${getStrengthWidth()}`}></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirm New Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    required
                                    className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:z-10 sm:text-sm"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    placeholder="Confirm your new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">Password Requirements:</h4>
                        <ul className="text-xs text-blue-800 space-y-1">
                            <li className="flex items-center">
                                <CheckCircle className={`w-3 h-3 mr-2 ${newPassword.length >= 8 ? 'text-green-600' : 'text-gray-400'}`} />
                                At least 8 characters
                            </li>
                            <li className="flex items-center">
                                <CheckCircle className={`w-3 h-3 mr-2 ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`} />
                                At least one uppercase letter
                            </li>
                            <li className="flex items-center">
                                <CheckCircle className={`w-3 h-3 mr-2 ${/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`} />
                                At least one lowercase letter
                            </li>
                            <li className="flex items-center">
                                <CheckCircle className={`w-3 h-3 mr-2 ${/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`} />
                                At least one number
                            </li>
                            <li className="flex items-center">
                                <CheckCircle className={`w-3 h-3 mr-2 ${/[^a-zA-Z0-9]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`} />
                                At least one special character (!@#$%^&*)
                            </li>
                        </ul>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
                            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                            <span className="text-sm text-red-800">{error}</span>
                        </div>
                    )}

                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-200"
                            style={{
                                backgroundColor: '#69BE28',
                                '--tw-ring-color': 'rgba(105, 190, 40, 0.5)'
                            } as React.CSSProperties}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin -ml-1 mr-3 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                    Changing Password...
                                </div>
                            ) : (
                                'Change Password'
                            )}
                        </button>
                    </div>

                    {/* Logout Option */}
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="text-sm text-gray-600 hover:text-gray-900"
                        >
                            Logout instead
                        </button>
                    </div>
                </form>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ChangePassword;